import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { BirthPrintView, DeathPrintView, PrintedPatientView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
} from '@wodi/ui';
import { useRef, type ReactNode, type Ref } from 'react';

import {
  birthPrintQuery,
  deathPrintQuery,
  markBirthPrinted,
  markDeathPrinted,
} from '../api/vital-events.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import styles from './vital-events.module.css';
import {
  CAUSE_ROLE_WORDS,
  errorText,
  gramsText,
  MANNER_WORDS,
  PLACE_WORDS,
  PROVISIONAL_LINE,
} from './words.ts';

// Printed death and birth notification summaries (A4, black on white;
// docs/plans/phase2-mortuary-and-notifications.md §1.1 item 10). Marked provisional until the
// official Cap. 149 forms are confirmed (TODO(verify-spec) D1, N1): no form numbers or field
// layouts are invented. Every print calls its `…/printed` route, which counts and audits it.

function copyTitle(base: string, copy: number): string {
  return copy > 1 ? `${base}, copy ${String(copy)}` : base;
}

function personLines(prefix: string, p: PrintedPatientView) {
  return [
    { label: `${prefix}name`, value: p.name },
    { label: `${prefix}patient no.`, value: p.patientNumber, identifier: true },
    {
      label: `${prefix}sex`,
      value: p.sexDisplay,
    },
    {
      label: `${prefix}date of birth`,
      value:
        p.birthDate === null
          ? 'Not recorded'
          : `${formatIsoDate(p.birthDate)}${p.birthDateEstimated ? ' (estimated)' : ''}`,
    },
  ];
}

export function DeathSummarySheet({
  print,
  sheetRef,
}: {
  print: DeathPrintView;
  sheetRef?: Ref<HTMLElement>;
}) {
  const d = print.death;
  return (
    <PrintSheet
      paper="a4"
      label="Death notification summary"
      {...(sheetRef === undefined ? {} : { ref: sheetRef })}
    >
      <PrintHeader
        facility={print.facility.name}
        title={copyTitle('Death notification summary', print.copy)}
      />
      <p className={styles.printText}>{PROVISIONAL_LINE}</p>
      <PrintLines
        items={[
          ...personLines('', print.patient),
          { label: 'Date and time of death', value: formatInstant(d.diedAt) },
          { label: 'Place of death', value: PLACE_WORDS[d.place] },
          { label: 'Manner of death', value: d.manner === null ? '' : MANNER_WORDS[d.manner] },
          ...d.causes.map((c) => ({
            label: `${CAUSE_ROLE_WORDS[c.role]}${c.role === 'contributing' ? ` ${String(c.position)}` : ''}`,
            value: [
              c.code === null ? null : `${c.code} ${c.display ?? ''}`,
              c.clinicianText,
              c.onsetInterval === null ? null : `onset ${c.onsetInterval} before death`,
            ]
              .filter((x) => x !== null)
              .join('; '),
          })),
        ]}
      />
      <PrintFooter>
        {`Certifying clinician: ${d.certifiedBy?.displayName ?? ''}`}
        <br />
        {'Signature: ____________________'}
        <br />
        {'Facility stamp:'}
      </PrintFooter>
    </PrintSheet>
  );
}

export function BirthSummarySheet({
  print,
  sheetRef,
}: {
  print: BirthPrintView;
  sheetRef?: Ref<HTMLElement>;
}) {
  const b = print.birth;
  return (
    <PrintSheet
      paper="a4"
      label="Birth notification summary"
      {...(sheetRef === undefined ? {} : { ref: sheetRef })}
    >
      <PrintHeader
        facility={print.facility.name}
        title={copyTitle('Birth notification summary', print.copy)}
      />
      <p className={styles.printText}>{PROVISIONAL_LINE}</p>
      <PrintLines
        items={[
          { label: 'Date and time of birth', value: formatInstant(b.bornAt) },
          { label: 'Outcome', value: 'Live birth' },
          ...personLines('Child: ', print.baby),
          { label: 'Birth weight', value: gramsText(b.birthWeightGrams) },
          ...(b.birthOrder === null ? [] : [{ label: 'Birth order', value: String(b.birthOrder) }]),
          ...personLines('Mother: ', print.mother),
        ]}
      />
      <PrintFooter>
        {`Attended by: ${b.attendant.displayName}`}
        <br />
        {'Signature: ____________________'}
        <br />
        {'Facility stamp:'}
      </PrintFooter>
    </PrintSheet>
  );
}

function Printer({
  label,
  query,
  mark,
  listKeys,
  render,
}: {
  label: string;
  query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  mark: () => Promise<unknown>;
  listKeys: readonly QueryKey[];
  render: (ref: Ref<HTMLElement>) => ReactNode;
}) {
  const sheet = useRef<HTMLElement>(null);
  const queryClient = useQueryClient();
  const printed = useMutation({
    mutationFn: mark,
    onSuccess: () => {
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  if (query.isPending) return <LoadingBlock label="Loading the summary" />;
  if (query.isError)
    return (
      <Notice tone="error" action={<Button onPress={() => void query.refetch()}>Try again</Button>}>
        {errorText(query.error, 'The summary could not load. Try again.')}
      </Notice>
    );
  return (
    <div className={styles.printer}>
      <div className={styles.preview}>{render(sheet)}</div>
      {printed.isError ? (
        <Notice tone="error">
          The summary printed, but Wodi could not count the copy. Check the connection.
        </Notice>
      ) : null}
      <Button
        variant="primary"
        onPress={() => {
          if (sheet.current === null) return;
          printSheet(sheet.current);
          printed.mutate();
        }}
      >
        {label}
      </Button>
    </div>
  );
}

export function DeathSummaryPrinter({ deathId }: { deathId: string }) {
  const query = useQuery(deathPrintQuery(deathId));
  return (
    <Printer
      label="Print notification summary"
      query={query}
      mark={() => markDeathPrinted(deathId)}
      listKeys={[['deaths']]}
      render={(ref) =>
        query.data === undefined ? null : <DeathSummarySheet print={query.data} sheetRef={ref} />
      }
    />
  );
}

export function BirthSummaryPrinter({ birthId }: { birthId: string }) {
  const query = useQuery(birthPrintQuery(birthId));
  return (
    <Printer
      label="Print notification summary"
      query={query}
      mark={() => markBirthPrinted(birthId)}
      listKeys={[['births']]}
      render={(ref) =>
        query.data === undefined ? null : <BirthSummarySheet print={query.data} sheetRef={ref} />
      }
    />
  );
}
